import { MissionDto } from '../api/promoMissionApi';
import { formatCurrency } from '@/shared/utils/formatter';
import { calcDays } from '@/shared/utils/dateUtils';
import {
    Mission, MissionType, PeriodType, TargetType, MissionStatus,
    FormState, MissionTypeCode, PeriodCode, EndedMissionAnalysis,
    FALLBACK_MISSION_TYPE_CODES,
} from '../types/promotion.types';

export function toMission(dto: MissionDto): Mission {
    return {
        id: dto.seqNo,
        seqNo: dto.seqNo,
        suppId: dto.suppId,
        title: dto.title,
        missionType: dto.missionType as MissionType,
        period: dto.periodType as PeriodType,
        targetCount: dto.targetCnt,
        rewardAmount: dto.rewardAmt,
        maxCount: dto.maxCnt,
        startDate: dto.startDate,
        endDate: dto.endDate,
        description: dto.description ?? '',
        targetType: dto.targetType as TargetType,
        targetRiders: dto.targetRiders ?? [],
        status: dto.statusCode as MissionStatus,
        actualResult: dto.actualResult
            ? {
                totalAchievers: dto.actualResult.totalAchievers,
                totalPayout: dto.actualResult.totalPayout,
                periodAchievers: dto.actualResult.periodAchievers,
                periodPayout: dto.actualResult.periodPayout,
            }
            : undefined,
    };
}

export function calcOpportunities(days: number, period: PeriodType): number {
    if (days <= 0) return 0;
    return period === 'DAILY' ? days : Math.ceil(days / 7);
}

export function getMissionMeta(code: MissionType, types: MissionTypeCode[]): MissionTypeCode {
    return (
        types.find((t) => t.code === code) ??
        FALLBACK_MISSION_TYPE_CODES.find((t) => t.code === code)!
    );
}

export function getPeriodName(code: PeriodType, periods: PeriodCode[]): string {
    return periods.find((p) => p.code === code)?.name ?? code;
}

export function buildPreviewMission(
    form: FormState,
    types: MissionTypeCode[],
    periods: PeriodCode[]
): string {
    const reward = formatCurrency(form.rewardAmount) || 'Y';
    const target = form.targetCount || 'X';
    const max =
        form.maxCount && form.maxCount !== '0'
            ? `최대 ${formatCurrency(form.maxCount)}건`
            : '무제한';
    const periodName = getPeriodName(form.period, periods);

    if (form.missionType === 'OVERAGE')
        return `${periodName} ${target}건 이상 수행 시 초과 1건당 ${reward}원 지급 (${max})`;
    if (form.missionType === 'FIXED')
        return `${periodName} ${target}건 이상 수행 시 ${reward}원 일시 지급`;
    return `${periodName} ${target}건 이상 달성 시 전체 건수 × ${reward}원 지급 (${max})`;
}

export function buildPreviewSchedule(form: FormState, periods: PeriodCode[]): string {
    const start = form.startDate || '시작일';
    const end = form.endDate || '종료일';
    const days = calcDays(form.startDate, form.endDate);
    const opp = calcOpportunities(days, form.period);
    const periodName = getPeriodName(form.period, periods);
    const dayStr = days > 0 ? ` (총 ${days}일)` : '';
    const oppStr = opp > 0 ? `, 총 ${opp}회 기회` : '';
    return `${start} ~ ${end}${dayStr} → ${periodName} 평가${oppStr}`;
}

export function buildPreviewReward(form: FormState): string {
    const reward = formatCurrency(form.rewardAmount) || 'Y';
    const target = form.targetCount || 'X';
    if (form.missionType === 'OVERAGE') return `${target}건 초과 시 초과 1건당 ${reward}원`;
    if (form.missionType === 'FIXED') return `${target}건 달성 시 ${reward}원 일시 지급`;
    return `${target}건 달성 시 전체 건수 × ${reward}원`;
}

export function buildEndedMissionAnalysis(m: Mission): EndedMissionAnalysis {
    const isOneDay = m.startDate === m.endDate;
    const r = m.actualResult;

    if (!r) {
        return {
            isOneDay,
            totalTargets: '미집계',
            totalPayout: '미집계',
            dailyTargets: '미집계',
            dailyPayout: '미집계',
        };
    }

    return {
        isOneDay,
        totalTargets: `${formatCurrency(r.totalAchievers)}명`,
        totalPayout: `${formatCurrency(r.totalPayout)}원`,
        dailyTargets: `${formatCurrency(r.periodAchievers)}명`,
        dailyPayout: `${formatCurrency(r.periodPayout)}원`,
    };
}

export function formatPeriodDate(startDate: string, endDate: string): string {
    const fmt = (d: string) =>
        `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
    return startDate === endDate
        ? fmt(startDate)
        : `${fmt(startDate)} ~ ${fmt(endDate)}`;
}
